import type { ReactNode } from 'react'

import { formatInteger, formatMultiple, formatPercent, formatUsd } from '../../demo/format'
import { useTodayMetrics } from '../../demo/selectors'
import { Card, CardHead, Icon, Term, type IconName } from '../../ui'
import styles from './TodayCard.module.css'

type DeltaKey = 'spend' | 'impressions' | 'clicks' | 'ctr' | 'cvr' | 'roas'

interface TodayMetric {
  key: DeltaKey
  icon: IconName
  label: ReactNode
  value: string
}

function deltaPresentation(value: number): { className: string; text: string } {
  if (value > 0) return { className: styles.up ?? '', text: `↑ ${formatPercent(value)}` }
  if (value < 0) return { className: styles.down ?? '', text: `↓ ${formatPercent(Math.abs(value))}` }
  return { className: styles.neutral ?? '', text: formatPercent(0) }
}

export function TodayCard() {
  const today = useTodayMetrics()
  const metrics: TodayMetric[] = [
    { key: 'spend', icon: 'dollar', label: '消耗金额', value: formatUsd(today.spend, 2) },
    { key: 'impressions', icon: 'eye', label: '展示次数', value: formatInteger(today.impressions) },
    { key: 'clicks', icon: 'target', label: '点击次数', value: formatInteger(today.clicks) },
    { key: 'ctr', icon: 'bar-chart', label: <Term k="ctr" />, value: formatPercent(today.ctr) },
    { key: 'cvr', icon: 'user', label: <Term k="cvr" />, value: formatPercent(today.cvr) },
    { key: 'roas', icon: 'trend', label: <Term k="roas" />, value: formatMultiple(today.roas) },
  ]

  return (
    <Card data-scope="overview-today">
      <CardHead title="今日概览" subtitle={`数据更新于 ${today.syncedAt}`} />
      <div className={styles.grid}>
        {metrics.map((metric) => {
          const delta = deltaPresentation(today.deltaPrev[metric.key])
          return (
            <div className={styles.metric} key={metric.key}>
              <p className={styles.label}><Icon name={metric.icon} size={15} strokeWidth={2} />{metric.label}</p>
              <p className={styles.value}>{metric.value}</p>
              <p className={styles.delta}><span className={delta.className}>{delta.text}</span> 较昨日</p>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
